import { Body } from "p2";

import { collisionGroups } from "../../config/CollisionGroups";
import { Colors } from "../../config/DesignConstants";
import BaseEntity from "../../core/entity/BaseEntity";
import Entity from "../../core/entity/Entity";
import { polarToVec } from "../../core/util/MathUtil";
import { choose, rDirection, rUniform } from "../../core/util/Random";
import { V, V2d } from "../../core/Vector";
import { DustExplosionEffect } from "../DustExplosionEffect";
import BrokenExpandEffect from "../effects/BrokenExpandEffect";
import { applyColor, applyThickness, VectorFilter } from "../graphics";
import VectorSprite from "../graphics/VectorSprite";
import { Hittable } from "../Hittable";
import { ShieldDamager } from "../interfaces/ShieldDamager";
import { LevelEntity } from "../LevelEntity";
import {
  ASTEROID_CONFIG,
  AsteroidShape,
  AsteroidSize,
  generateRandomAsteroidShape,
  getSmallerAsteroidSize,
} from "./AsteroidShapes";

// Asteroid physics constants
const ASTEROID_MASS = 100.0;
const GRAVITY_FORCE_STRENGTH = 4; // Force pulling toward turret
const INITIAL_VELOCITY_RANGE = 3; // Initial random velocity - reduced from 10
const ANGULAR_VELOCITY_RANGE = 1; // Random rotation speed - reduced from 2
const MAX_DISTANCE_FROM_CENTER = 500; // Destroy if too far away
const FRAGMENT_VELOCITY_BOOST = 15; // Extra velocity for fragments - reduced from 30

// Asteroid rendering constants
const STROKE_WIDTH = 1;
const FLASH_STROKE_WIDTH = 3;
const EXPLOSION_LIFETIME = 1.2;
const EXPLOSION_VELOCITY_SCALE = 20.0;
const FLASH_DURATION = 0.06;

// Dust trail constants
const DUST_SPAWN_INTERVAL_MIN = 0.2; // minimum seconds between dust spawns
const DUST_SPAWN_INTERVAL_MAX = 0.6; // maximum seconds between dust spawns
const DUST_PARTICLE_COUNT_MIN = 1; // minimum particles per spawn
const DUST_PARTICLE_COUNT_MAX = 2; // maximum particles per spawn
const DUST_EXPLOSION_SPEED = 4; // speed of dust particles (slower)
const MIN_VELOCITY_FOR_DUST = 3; // minimum asteroid velocity to spawn dust

export default class Asteroid
  extends LevelEntity
  implements Entity, Hittable, ShieldDamager
{
  body: NonNullable<BaseEntity["body"]>;
  private renderer: VectorSprite;

  size: AsteroidSize;
  shape: AsteroidShape;
  maxHealth: number;
  currentHealth: number;
  private dustTimer: number = 0;
  private nextDustInterval: number = rUniform(
    DUST_SPAWN_INTERVAL_MIN,
    DUST_SPAWN_INTERVAL_MAX,
  );

  constructor(position: V2d, size: AsteroidSize, initialVelocity?: V2d) {
    super();
    this.tags = ["asteroid", "levelEntity"];

    this.size = size;
    this.shape = generateRandomAsteroidShape(size);
    this.maxHealth = ASTEROID_CONFIG[size].health;
    this.currentHealth = this.maxHealth;

    // Create visual renderer
    this.renderer = VectorSprite.fromPolygon(
      this.shape.points,
      Colors.WHITE,
      STROKE_WIDTH,
    );
    this.addChild(this.renderer);

    // Create physics body with polygon collision that matches visual shape
    this.body = new Body({ mass: ASTEROID_MASS });

    // Use fromPolygon since the shape might be concave
    this.body.fromPolygon(this.shape.points);

    for (const shape of this.body.shapes) {
      shape.collisionGroup = collisionGroups("Enemy");
      shape.collisionMask = collisionGroups("Turret", "Bullet", "Explosion");
    }

    this.body.position = position.clone();

    // Set initial velocity - either provided or random
    this.body.velocity =
      initialVelocity ??
      polarToVec(rDirection(), rUniform(0, INITIAL_VELOCITY_RANGE));

    // Random angular velocity for spinning
    this.body.angularVelocity = rUniform(
      -ANGULAR_VELOCITY_RANGE,
      ANGULAR_VELOCITY_RANGE,
    );
  }

  onTick(dt: number) {
    // Apply gravity force toward turret (like gravity)
    this.body.applyForce(
      V(this.body.position).imul(-1).imul(GRAVITY_FORCE_STRENGTH),
    );

    // Destroy asteroid if it gets too far from center
    const distanceToCenter = V(this.body.position).magnitude;
    if (distanceToCenter > MAX_DISTANCE_FROM_CENTER) {
      this.destroy();
      return;
    }

    // Update dust trail timer
    this.dustTimer += dt;
    if (this.dustTimer >= this.nextDustInterval) {
      this.dustTimer = 0;
      this.nextDustInterval = rUniform(
        DUST_SPAWN_INTERVAL_MIN,
        DUST_SPAWN_INTERVAL_MAX,
      );
      this.spawnDustTrail();
    }
  }

  onRender() {
    if (this.renderer) {
      this.renderer.setPosition(this.body.position);
      this.renderer.setRotation(this.body.angle);
    }
  }

  takeHit(damage: number) {
    this.currentHealth -= damage;

    if (this.currentHealth <= 0) {
      this.die();
    } else {
      this.renderer.addFilter(
        new VectorFilter(
          [applyColor(Colors.WHITE), applyThickness(FLASH_STROKE_WIDTH)],
          FLASH_DURATION,
        ),
      );

      // Dispatch impact event for non-fatal hit
      this.dispatch("bulletImpact", {
        targetType: "asteroid" as const,
        position: this.getPosition(),
        damage: damage,
        remainingHealth: this.currentHealth,
      });
    }
  }

  private die(explodeDirection: V2d = V(0, 0)) {
    // Add score
    this.dispatch("scoreAdded", { points: ASTEROID_CONFIG[this.size].points });

    // Dispatch asteroid destroyed event for sound effects
    this.dispatch("asteroidDestroyed", {
      asteroidSize: this.size,
      points: ASTEROID_CONFIG[this.size].points,
    });

    this.createDeathParticles(explodeDirection);
    this.createFragments(explodeDirection);
    this.destroy();
  }

  private createDeathParticles(explodeDirection: V2d = V(0, 0)) {
    // Create rotated points for explosion effect
    const currentRotation = this.body?.angle || 0;
    const rotatedPoints = this.shape.points.map((point) =>
      point.rotate(currentRotation),
    );

    // Get current velocity for inheritance
    const currentVelocity = V(this.body.velocity);
    const position = this.getPosition();
    const currentLevel = this.getCurrentLevel();

    // Create explosion using asteroid's rotated shape and white color
    const explosion = new BrokenExpandEffect({
      position,
      polygon: rotatedPoints,
      color: Colors.WHITE,
      lifetime: EXPLOSION_LIFETIME,
      velocityScale: EXPLOSION_VELOCITY_SCALE,
      inheritedVelocity: currentVelocity,
    });

    // Add explosion to the current level
    currentLevel?.addChild(explosion);

    // Create dust explosion effect with size-based particle count
    const particleCount = (() => {
      switch (this.size) {
        case AsteroidSize.SMALL:
          return 100;
        case AsteroidSize.MEDIUM:
          return 150;
        case AsteroidSize.LARGE:
          return 200;
        case AsteroidSize.XL:
          return 300;
        case AsteroidSize.XXL:
          return 400;
        default:
          return 100;
      }
    })();

    const dustExplosion = new DustExplosionEffect({
      position,
      particleCount,
      color: Colors.WHITE,
      polygonVertices: rotatedPoints,
    });

    currentLevel?.addChild(dustExplosion);
  }

  private createFragments(explodeDirection: V2d = V(0, 0)) {
    const smallerSize = getSmallerAsteroidSize(this.size);
    const currentLevel = this.getCurrentLevel();
    if (currentLevel && smallerSize !== null) {
      const fragmentCount = choose(2, 3, 3, 4, 4, 5);
      const position = this.getPosition();
      const currentVelocity = V(this.body?.velocity || [0, 0]);

      for (let i = 0; i < fragmentCount; i++) {
        // Create random velocity for fragment (inherit some + add random)
        const randomAngle = rDirection();
        const randomSpeed = rUniform(
          FRAGMENT_VELOCITY_BOOST * 0.5,
          FRAGMENT_VELOCITY_BOOST,
        );
        const fragmentVelocity = currentVelocity
          .mul(0.3)
          .iadd(polarToVec(randomAngle, randomSpeed))
          .iadd(explodeDirection);

        // Create fragment asteroid

        currentLevel.addChild(
          new Asteroid(position, smallerSize, fragmentVelocity),
        );
      }
    }
  }

  private spawnDustTrail(): void {
    const velocity = V(this.body.velocity);

    // Only spawn dust if asteroid is moving fast enough
    if (velocity.magnitude < MIN_VELOCITY_FOR_DUST) {
      return;
    }

    const position = this.getPosition();
    const currentLevel = this.getCurrentLevel();

    if (!currentLevel) {
      return;
    }

    // Get current rotation and create rotated polygon vertices for edge spawning
    const currentRotation = this.body?.angle || 0;
    const rotatedVertices = this.shape.points.map((point) =>
      point.rotate(currentRotation),
    );

    // Create dust particles trailing behind the asteroid from its edges
    const dustDirection = velocity.normalize(-1); // Opposite direction of movement

    // Random particle count for natural variation
    const particleCount = Math.floor(
      rUniform(DUST_PARTICLE_COUNT_MIN, DUST_PARTICLE_COUNT_MAX + 1),
    );

    const dustExplosion = new DustExplosionEffect({
      position,
      particleCount,
      explosionSpeed: DUST_EXPLOSION_SPEED,
      color: Colors.WHITE,
      direction: dustDirection,
      polygonVertices: rotatedVertices, // Spawn from asteroid edges
    });

    currentLevel.addChild(dustExplosion);
  }

  // ShieldDamager interface implementation
  getShieldDamage(): number {
    return 1; // Asteroids deal 1 damage to shield
  }

  onShieldHit(): void {
    this.die(this.getPosition());
  }
}
